import React, { useState, useCallback } from "react";
import { toast } from "react-toastify";
import {
  useRegisteredDonorsList,
  useUpdateDonorStatus,
} from "../../../hooks/useAdminDonors";
import DataTable from "../../../components/admin/DataTable";
import Pagination from "../../../components/admin/Pagination";
import SearchFilter from "../../../components/admin/SearchFilter";
import RegisteredDonorEditModal from "./RegisteredDonorEditModal";

const BLOOD_GROUPS = ["A POSITIVE(+)", "A NEGATIVE(-)", "B POSITIVE(+)", "B NEGATIVE(-)", "O POSITIVE(+)", "O NEGATIVE(-)", "AB POSITIVE(+)", "AB NEGATIVE(-)"];
const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const AdminRegisteredDonors = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ bloodGroup: "", status: "" });
  const [editDonor, setEditDonor] = useState(null);

  const queryParams = {
    page,
    limit: 20,
    ...(search && { search }),
    ...(filters.bloodGroup && { bloodGroup: filters.bloodGroup }),
    ...(filters.status && { status: filters.status }),
  };

  const { data, isLoading, error } = useRegisteredDonorsList(queryParams);
  const statusMutation = useUpdateDonorStatus();

  const handleSearchChange = useCallback((val) => {
    setSearch(val);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((name, val) => {
    setFilters((prev) => ({ ...prev, [name]: val }));
    setPage(1);
  }, []);

  const handleToggleStatus = async (donor) => {
    const newStatus = donor.status === "active" ? "inactive" : "active";
    try {
      await statusMutation.mutateAsync({ id: donor._id, status: newStatus });
      toast.success(`${donor.name} is now ${newStatus}`);
    } catch (err) {
      toast.error(err.message || "Failed to update status");
    }
  };

  const columns = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "number", label: "Phone" },
    { key: "group", label: "Blood Group" },
    { key: "district", label: "District" },
    { key: "status", label: "Status" },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <div className="flex gap-1">
          <button
            className="btn btn-xs btn-info text-white"
            onClick={() => setEditDonor(row)}
          >
            Edit
          </button>
          <button
            className={`btn btn-xs ${row.status === "active" ? "btn-warning" : "btn-success"} text-white`}
            onClick={() => handleToggleStatus(row)}
          >
            {row.status === "active" ? "Deactivate" : "Activate"}
          </button>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="alert alert-error">
        <span>Failed to load donors: {error.message}</span>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Registered Donors</h1>

      <SearchFilter
        searchValue={search}
        onSearchChange={handleSearchChange}
        filters={[
          {
            name: "bloodGroup",
            label: "All Blood Groups",
            value: filters.bloodGroup,
            options: BLOOD_GROUPS.map((g) => ({ value: g, label: g })),
          },
          {
            name: "status",
            label: "All Status",
            value: filters.status,
            options: STATUS_OPTIONS,
          },
        ]}
        onFilterChange={handleFilterChange}
      />

      <DataTable columns={columns} data={data?.data} loading={isLoading} emptyMessage="No registered donors found" />

      {data?.pagination && (
        <div className="flex justify-between items-center mt-4">
          <span className="text-sm text-gray-700">
            Showing {((data.pagination.page - 1) * data.pagination.limit) + 1}–
            {Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)} of{" "}
            {data.pagination.total}
          </span>
          <Pagination
            currentPage={data.pagination.page}
            totalPages={data.pagination.totalPages}
            onPageChange={setPage}
          />
        </div>
      )}

      {editDonor && (
        <RegisteredDonorEditModal
          donor={editDonor}
          onClose={() => setEditDonor(null)}
        />
      )}
    </div>
  );
};

export default AdminRegisteredDonors;
